// Sugalarm — nav rail (left edge). Used by the web frame and (cycle 77)
// the kiosk side-nav mode.
//
// Cycle 78: the rail is **icon-only**. The text label that used to sit
// under each glyph is gone; instead each item exposes its label as a
// hover/focus **flyout** to the right of the icon, plus an `aria-label`
// for screen readers.
//
// Cycle 103: the rail is a **detached pill** — a translucent
// rounded-rectangle (the same `--kiosk-nav-fill` + blur + hairline +
// floating shadow the bottom-mode `FooterTabs` uses) hugging the five-icon
// stack, flush to the left screen edge (square, border-less left edge)
// with `--kiosk-panel-radius` top-right/bottom-right corners. The curved
// cycle-79/86 header↔rail seam is gone.
//
// Cycle 103a (follow-up): the pill is **vertically centered** in the rail
// column (was bottom-aligned), and the **web** variant now renders the
// SAME pill as the kiosk so the web nav matches the kiosk look. Every
// kiosk-scoped token carries a solid fallback so the pill renders
// correctly on the web frame too (no `.kiosk` ancestor there). The rail
// COLUMN still reserves `--kiosk-rail-width` of layout width so the
// content region's geometry is unchanged; only the visible background is
// the pill. The 5 s flyout auto-fade stays scoped to the kiosk variant
// (the web rail has a mouse, so hover-out already dismisses the flyout —
// an auto-fade would hide a label the user is still hovering).

import { useAttributionPickerOpen } from "../../lib/attributionPicker";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { PHOTO_BLOCKED_LINE, usePhotoBlockedByAlarm } from "../../lib/nativeCamera";
import { usePhotoCaptureAction } from "../../hooks/usePhotoCaptureAction";
import { usePhotoEnabled } from "../../hooks/usePhotoEnabled";
import { enterClockMode } from "../../lib/clockMode";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import { NAV_ITEMS, NavIcon, type NavId } from "./NavIcon";

interface Props {
  // Cycle 42: retained on the prop surface (callers still pass it and
  // a future cycle may surface it elsewhere on the rail) but no longer
  // rendered — the Alerts nav entry it badged is gone.
  unackCount: number;
  // "web" (default) is the web-frame rail; "kiosk" is the kiosk side-nav
  // mode. Both render the same detached pill (cycle 103a); the only
  // behavioral difference is the kiosk-only 5 s flyout auto-fade.
  variant?: "web" | "kiosk";
}

export function EdgeRail({ variant = "web" }: Props) {
  const kiosk = variant === "kiosk";
  // Cycle 456: the kiosk rail dims while the "Who's treating?" sheet is up.
  const pickerDim = useAttributionPickerOpen() && kiosk;
  // Cycle 199: on the Android-tablet kiosk the hover/focus flyout labels
  // are not rendered (a touch panel + native chrome makes them stale
  // tooltips), and the 5 s auto-fade timer is skipped so no stale timer
  // machinery runs. The accessible name survives via each item's
  // `aria-label`. The Pi kiosk keeps the cycle-103 touch auto-fade and the
  // web rail keeps its hover flyout — both unchanged.
  const noFlyout = kiosk && isAndroidTabletClassRuntime();
  const location = useLocation();
  // Camera ACTION item — take a photo of food straight into the Log, from
  // the rail. Only offered on the kiosk side rail (never the web rail) and
  // only where the kiosk actually has a camera (the Android-tablet class —
  // the Pi/iPad kiosk stay excluded, same gate LogScreen's photo CTA uses),
  // and only once the server confirms the photo feature is configured.
  const photoEnabled = usePhotoEnabled();
  const showCamera = kiosk && isAndroidTabletClassRuntime() && photoEnabled;
  const photoCapture = usePhotoCaptureAction(true);
  // Cycle 455: disabled on the kiosk while any alarm sounds (lib/nativeCamera).
  const photoBlocked = usePhotoBlockedByAlarm();
  // Which item's label flyout is currently shown (mouse hover or keyboard
  // focus / touch focus). `flyoutTick` bumps on every show so re-focusing
  // the SAME item also restarts the kiosk auto-fade timer (a state value
  // that doesn't change wouldn't re-run the effect on its own).
  const [flyoutId, setFlyoutId] = useState<NavId | null>(null);
  const [flyoutTick, setFlyoutTick] = useState(0);

  const showFlyout = (id: NavId) => {
    if (noFlyout) return;
    setFlyoutId(id);
    setFlyoutTick((t) => t + 1);
  };
  const hideFlyout = (id: NavId) => {
    if (noFlyout) return;
    setFlyoutId((cur) => (cur === id ? null : cur));
  };

  // Cycle 103: on the kiosk a tap focuses an item and — with no hover on
  // the touch panel — the flyout would linger indefinitely. Start a 5 s
  // timer when a flyout appears and clear it (the existing 120 ms opacity
  // fade plays it out). The cleanup runs on unmount and on every
  // flyout change, so re-showing (a new tap on the same or another item,
  // tracked via `flyoutTick`) resets the timer and a stale timeout can
  // never hide a freshly shown label. Scoped to the kiosk variant — the
  // web rail's hover-out / blur already dismisses the flyout.
  useEffect(() => {
    if (!kiosk || noFlyout || flyoutId == null) return;
    const t = window.setTimeout(() => setFlyoutId(null), 5000);
    return () => window.clearTimeout(t);
  }, [kiosk, noFlyout, flyoutId, flyoutTick]);

  const items = NAV_ITEMS.map((n) => (
    <NavLink
      key={n.id}
      to={n.path}
      aria-label={n.label}
      onMouseEnter={() => showFlyout(n.id)}
      onMouseLeave={() => hideFlyout(n.id)}
      onFocus={() => showFlyout(n.id)}
      onBlur={() => hideFlyout(n.id)}
      style={({ isActive }) => ({
        appearance: "none",
        border: 0,
        background: isActive ? "var(--accent-soft)" : "transparent",
        color: isActive ? "var(--accent-ink)" : "var(--ink-3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Cycle 103a: kiosk + web share one sizing now (web matches the
        // kiosk look). Vertical spacing comes from the pill's `gap` + its
        // even top/bottom padding, so items carry no margin.
        padding: "10px 6px",
        margin: 0,
        borderRadius: 10,
        position: "relative",
        minHeight: 56,
        textDecoration: "none",
      })}
    >
      {({ isActive }) => (
        <>
          <NavIcon id={n.id} size={26} active={isActive} />
          {/* Label flyout — visual only (the NavLink carries the
              accessible name via `aria-label`), so it's aria-hidden to
              avoid a double announcement. Shown on hover/focus; on the
              kiosk it fades out 5 s after appearing (cycle 103). Cycle 199:
              not rendered at all on the Android-tablet kiosk. */}
          {!noFlyout && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: "calc(100% + 8px)",
              top: "50%",
              transform: "translateY(-50%)",
              padding: "6px 10px",
              borderRadius: 8,
              background: "var(--bg-elev)",
              color: "var(--ink)",
              border: "0.5px solid var(--line-strong)",
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.28)",
              fontSize: "var(--type-floor)",
              fontWeight: 600,
              letterSpacing: "0.01em",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              opacity: flyoutId === n.id ? 1 : 0,
              transition: "opacity 120ms ease",
              zIndex: 20,
            }}
          >
            {n.label}
          </span>
          )}
        </>
      )}
    </NavLink>
  ));

  return (
    // The rail COLUMN reserves the full `--kiosk-rail-width` of layout
    // width + full height so the content region's geometry is unchanged —
    // but it's transparent and border-less; only the pill is painted.
    // `justify-content: center` floats the pill vertically centered
    // (cycle 103a, was bottom-aligned). Token carries a 72px fallback so
    // the web frame (no `.kiosk` ancestor) reserves the same width.
    <nav
      style={{
        width: "var(--kiosk-rail-width, 72px)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        background: "transparent",
        flexShrink: 0,
        // Establish a stacking context above the content area so each
        // item's flyout overlays the route content to its right.
        position: "relative",
        zIndex: 10,
        // Cycle 456: dimmed and inert while "Who's treating?" is up on the
        // kiosk (a ≤ 45 s question; Skip or a pick gives the rail back).
        ...(pickerDim
          ? { opacity: 0.35, pointerEvents: "none" as const, transition: "opacity 160ms" }
          : {}),
      }}
      aria-disabled={pickerDim || undefined}
      data-picker-dimmed={pickerDim || undefined}
    >
      <div
        // The detached pill. Flush to x = 0 (the left screen edge): square
        // left corners, no left border. Rounded right corners
        // (`--kiosk-panel-radius`, shared with the chart card + FooterTabs)
        // snug around the icon stack. Treated like the bottom-mode nav —
        // translucent `--kiosk-nav-fill`, backdrop blur, the hairline
        // border (top/right/bottom only — nothing on the flush left edge),
        // and the floating shadow — so it reads correctly over a photo
        // background exactly as the bottom nav does. Even top/bottom
        // padding + `gap` keep it snug around the five icons; default flex
        // `align-items: stretch` makes the pill (and each item) fill the
        // rail width, so the glyphs sit centered + evenly inset. Every
        // kiosk-scoped token has a solid fallback so the SAME pill renders
        // on the web frame (cycle 103a) where `.kiosk` isn't an ancestor.
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 8,
          background: "var(--kiosk-nav-fill, rgba(0, 0, 0, 0.55))",
          // Chromium-on-Pi needs the -webkit- prefix; the unprefixed
          // property is the modern fallback.
          WebkitBackdropFilter: "blur(14px) saturate(140%)",
          backdropFilter: "blur(14px) saturate(140%)",
          borderTop: "0.5px solid var(--line-strong)",
          borderRight: "0.5px solid var(--line-strong)",
          borderBottom: "0.5px solid var(--line-strong)",
          borderTopRightRadius: "var(--kiosk-panel-radius, 14px)",
          borderBottomRightRadius: "var(--kiosk-panel-radius, 14px)",
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          boxShadow: "0 6px 24px rgba(0, 0, 0, 0.18)",
        }}
      >
        {/* Cycle 146: the Clock ACTION item slots BEFORE Settings so
            Settings stays the last item. Still not a routed tab (no
            active state); NAV_ITEMS and the five tabs are untouched. */}
        {items.slice(0, -1)}
        {showCamera && (
          <>
            <input
              ref={photoCapture.inputRef}
              type="file"
              accept={photoCapture.accept}
              capture={photoCapture.capture}
              onChange={photoCapture.onChange}
              style={{ display: "none" }}
            />
            <button
              type="button"
              aria-label={photoBlocked ? `Photo of meal. ${PHOTO_BLOCKED_LINE}` : "Photo of meal"}
              disabled={photoBlocked}
              onMouseEnter={() => showFlyout("camera")}
              onMouseLeave={() => hideFlyout("camera")}
              onFocus={() => showFlyout("camera")}
              onBlur={() => hideFlyout("camera")}
              onClick={photoCapture.open}
              style={{
                appearance: "none",
                border: 0,
                background: "transparent",
                color: photoBlocked ? "var(--ink-4)" : "var(--ink-3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 6px",
                margin: 0,
                borderRadius: 10,
                position: "relative",
                minHeight: 56,
                cursor: photoBlocked ? "default" : "pointer",
              }}
            >
              <NavIcon id="camera" size={26} />
              {!noFlyout && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: "calc(100% + 8px)",
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "6px 10px",
                    borderRadius: 8,
                    background: "var(--bg-elev)",
                    color: "var(--ink)",
                    border: "0.5px solid var(--line-strong)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.28)",
                    fontSize: "var(--type-floor)",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    opacity: flyoutId === "camera" ? 1 : 0,
                    transition: "opacity 120ms ease",
                    zIndex: 20,
                  }}
                >
                  Photo of meal
                </span>
              )}
            </button>
          </>
        )}
        <button
          type="button"
          aria-label="Alarm clock mode"
          onMouseEnter={() => showFlyout("clock")}
          onMouseLeave={() => hideFlyout("clock")}
          onFocus={() => showFlyout("clock")}
          onBlur={() => hideFlyout("clock")}
          onClick={() =>
            enterClockMode("nav", { priorPath: location.pathname })
          }
          style={{
            appearance: "none",
            border: 0,
            background: "transparent",
            color: "var(--ink-3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px 6px",
            margin: 0,
            borderRadius: 10,
            position: "relative",
            minHeight: 56,
            cursor: "pointer",
          }}
        >
          <NavIcon id="clock" size={26} />
          {!noFlyout && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: "calc(100% + 8px)",
              top: "50%",
              transform: "translateY(-50%)",
              padding: "6px 10px",
              borderRadius: 8,
              background: "var(--bg-elev)",
              color: "var(--ink)",
              border: "0.5px solid var(--line-strong)",
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.28)",
              fontSize: "var(--type-floor)",
              fontWeight: 600,
              letterSpacing: "0.01em",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              opacity: flyoutId === "clock" ? 1 : 0,
              transition: "opacity 120ms ease",
              zIndex: 20,
            }}
          >
            Clock
          </span>
          )}
        </button>
        {items.slice(-1)}
      </div>
    </nav>
  );
}
